import * as THREE from "three";

// Hand-drawn / wojak vibe: Comic Sans (with comic fallbacks) for all in-world
// canvas text — billboards + name plates. Uses system Comic Sans MS where
// present; falls back to other comic/handwritten faces, then cursive.
export const COMIC_FONT =
  '"Comic Sans MS", "Comic Neue", "Chalkboard SE", "Marker Felt", cursive';

function wrap(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// A white wall panel (black border) with a bold title, a wrapped body, and an
// optional footer — for expert-topic and methodology billboards.
export function textPanel(
  opts: { title: string; body?: string; footer?: string },
  planeWidth: number,
  planeHeight: number,
): THREE.Mesh {
  const W = 1024;
  const H = Math.round(W * (planeHeight / planeWidth));
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 14;
  ctx.strokeRect(12, 12, W - 24, H - 24);
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";

  const pad = 64;
  ctx.textBaseline = "top";
  const titleSize = Math.round(H * 0.11);
  ctx.font = `bold ${titleSize}px ${COMIC_FONT}`;
  ctx.fillText(opts.title, W / 2, pad, W - pad * 2);

  if (opts.body) {
    const fs = Math.round(H * 0.072);
    ctx.font = `${fs}px ${COMIC_FONT}`;
    let y = pad + titleSize + Math.round(H * 0.06);
    for (const line of wrap(ctx, opts.body, W - pad * 2).slice(0, 6)) {
      ctx.fillText(line, W / 2, y, W - pad * 2);
      y += Math.round(fs * 1.3);
    }
  }

  if (opts.footer) {
    ctx.textBaseline = "bottom";
    ctx.font = `italic ${Math.round(H * 0.06)}px ${COMIC_FONT}`;
    ctx.fillText(opts.footer, W / 2, H - pad, W - pad * 2);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(planeWidth, planeHeight),
    mat,
  );
  mesh.userData.skipEdges = true; // canvas already has its border
  return mesh;
}
